import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';

import { CompetitorsTab } from './components/competitors-tab/CompetitorsTab';
import { GeographicExposureTab } from './components/geographic-exposure-tab/GeographicExposureTab';
import { OverviewTab } from './components/overview-tab/OverviewTab';
import { ThemeExposureTab } from './components/theme-exposure-tab/ThemeExposureTab';
import * as S from './KnowledgeGraphWidget.styles';

export enum KnowledgeGraphTabType {
  Overview = 'overview',
  ThemeExposure = 'themeExposure',
  Competitors = 'competitors',
  GeographicExposure = 'geographicExposure',
}

interface KnowledgeGraphWidgetProps {
  entity: Entity;
}

export const KnowledgeGraphWidget = ({ entity }: KnowledgeGraphWidgetProps) => {
  const { t } = useTranslation(['widget', 'scenario']);
  const { params, setQueryParams } = useQueryParams();

  const knowledgeGraphTabs = [
    {
      tabLabel: t('assetOverview.knowledgeGraph.subtab.overview'),
      tabType: KnowledgeGraphTabType.Overview,
    },
    {
      tabLabel: t('assetOverview.knowledgeGraph.subtab.theme'),
      tabType: KnowledgeGraphTabType.ThemeExposure,
    },
    {
      tabLabel: t('assetOverview.knowledgeGraph.subtab.competitors'),
      tabType: KnowledgeGraphTabType.Competitors,
    },
    {
      tabLabel: t('assetOverview.knowledgeGraph.subtab.geographic'),
      tabType: KnowledgeGraphTabType.GeographicExposure,
    },
  ];

  const renderTabContent = (tab: string) => {
    switch (tab) {
      case KnowledgeGraphTabType.Overview:
        return <OverviewTab entity={entity} />;
      case KnowledgeGraphTabType.ThemeExposure:
        return <ThemeExposureTab entity={entity} />;
      case KnowledgeGraphTabType.Competitors:
        return <CompetitorsTab entity={entity} />;
      case KnowledgeGraphTabType.GeographicExposure:
        return <GeographicExposureTab entity={entity} />;
      default:
        return null;
    }
  };

  const onTabChange = (tab: KnowledgeGraphTabType) => {
    setQueryParams({
      [queryKeys.activeSubTab]: tab,
      [queryKeys.geoSubTab]: undefined,
    });
  };

  const requestedTab =
    params[queryKeys.activeSubTab] ?? KnowledgeGraphTabType.Overview;
  const activeTab = knowledgeGraphTabs.some(tab => tab.tabType === requestedTab)
    ? requestedTab
    : KnowledgeGraphTabType.ThemeExposure;

  const activeIndex = knowledgeGraphTabs.findIndex(
    tab => tab.tabType === activeTab
  );

  return (
    <S.Container>
      <S.StyledButtonGroup
        labels={knowledgeGraphTabs.map(t => t.tabLabel)}
        activeIndex={activeIndex >= 0 ? activeIndex : 0}
        onClick={tab => onTabChange(knowledgeGraphTabs[tab].tabType)}
      />
      {renderTabContent(activeTab)}
    </S.Container>
  );
};
